import { useMemo, useState, type ReactElement } from 'react';
import { Fingerprint } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { UserPicker, type UserPickerOption } from '@/components/ui/user-picker';
import { PageHeader } from '@/components/shell/PageHeader';

import {
  useFederatedIdentities,
  useFederatedIdentityMutations,
  type FederatedIdentityViewModel,
} from '@/features/admin-federated-identities/api';
import type { FederatedIdentityStatusDto } from '@/features/admin-federated-identities/endpoints';
import { usePlatformUserSearch } from '@/features/admin-federated-identities/usePlatformUserSearch';
import { DeleteAuthConfirmDialog } from '@/features/admin-auth/components/DeleteAuthConfirmDialog';
import { FederatedIdentitiesTable } from '@/features/admin-federated-identities/components/FederatedIdentitiesTable';
import { LinkIdentityDialog } from '@/features/admin-federated-identities/components/LinkIdentityDialog';

const STATUS_TABS: readonly FederatedIdentityStatusDto[] = ['PENDING', 'ACTIVE', 'RETIRED'];

export function AdminFederatedIdentitiesPage(): ReactElement {
  const { t } = useTranslation();
  const fp = 'routes.platform.federatedIdentities';

  const [status, setStatus] = useState<FederatedIdentityStatusDto>('PENDING');
  // A user filter only makes sense once identities have a linked user, so it's offered on the
  // Linked/Retired tabs, not on Pending.
  const showUserFilter = status !== 'PENDING';
  const [userFilter, setUserFilter] = useState<UserPickerOption | null>(null);
  const [userQuery, setUserQuery] = useState('');
  const { results: userResults, isLoading: userSearching } = usePlatformUserSearch(userQuery);

  const activeUserGuid = showUserFilter && userFilter !== null ? userFilter.guid : undefined;
  const query = useFederatedIdentities(
    activeUserGuid !== undefined ? { status, userGuid: activeUserGuid } : { status },
  );
  const { link, unlink } = useFederatedIdentityMutations();

  function selectStatus(next: FederatedIdentityStatusDto): void {
    setStatus(next);
    if (next === 'PENDING') {
      // Pending captures have no linked user — drop any active user filter.
      setUserFilter(null);
      setUserQuery('');
    }
  }

  const [linkTarget, setLinkTarget] = useState<FederatedIdentityViewModel | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<FederatedIdentityViewModel | null>(null);

  const rows = useMemo(
    () =>
      query.isSuccess
        ? [...query.data].sort((a, b) =>
            (a.capturedName || a.capturedEmail || a.externalSubject).localeCompare(
              b.capturedName || b.capturedEmail || b.externalSubject,
            ),
          )
        : [],
    [query.isSuccess, query.data],
  );

  function closeLink(): void {
    setLinkTarget(null);
    link.reset();
  }

  function closeUnlink(): void {
    setUnlinkTarget(null);
    unlink.reset();
  }

  const unlinkIsPending = unlinkTarget !== null;
  // A human-friendly reference for the confirm copy (email, else the opaque subject).
  const unlinkLabel =
    unlinkTarget === null ? '' : unlinkTarget.capturedEmail || unlinkTarget.externalSubject;

  return (
    <>
      <PageHeader
        icon={<Fingerprint className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t(`${fp}.heading`)}
        subtitle={t(`${fp}.subheading`)}
      />
      <div className="px-8 py-5 space-y-8 bg-background">
        <section aria-labelledby="federated-identities-heading" className="space-y-4">
          <header className="flex items-start justify-between gap-4">
            <div>
              <h2
                id="federated-identities-heading"
                className="text-base font-semibold text-foreground"
              >
                {t(`${fp}.listHeading`)}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">{t(`${fp}.listSubheading`)}</p>
            </div>
            <div
              className="flex items-center gap-1"
              role="group"
              aria-label={t(`${fp}.statusFilterLabel`)}
            >
              {STATUS_TABS.map((tab) => (
                <Button
                  key={tab}
                  size="sm"
                  variant={tab === status ? 'default' : 'outline'}
                  aria-pressed={tab === status}
                  onClick={() => {
                    selectStatus(tab);
                  }}
                >
                  {t(`${fp}.statusLabel.${tab}`)}
                </Button>
              ))}
            </div>
          </header>

          {showUserFilter && (
            <div className="max-w-sm space-y-1.5">
              <label htmlFor="identity-user-filter" className="text-xs font-medium text-muted-foreground">
                {t(`${fp}.userFilterLabel`)}
              </label>
              <UserPicker
                id="identity-user-filter"
                value={userFilter}
                onChange={setUserFilter}
                options={userResults}
                isLoading={userSearching}
                query={userQuery}
                onQueryChange={setUserQuery}
                placeholder={t(`${fp}.userFilterPlaceholder`)}
                ariaLabel={t(`${fp}.userFilterLabel`)}
              />
            </div>
          )}

          {query.isPending && <RowsSkeleton />}

          {query.isError && (
            <div className="max-w-xl">
              <FetchErrorCallout
                error={query.error}
                heading={t(`${fp}.error.heading`)}
                message={t(`${fp}.error.message`)}
                codeLabel={t(`${fp}.error.codeLabel`)}
                onRetry={() => {
                  query.refetch().catch(() => undefined);
                }}
                retryLabel={t(`${fp}.error.retry`)}
              />
            </div>
          )}

          {query.isSuccess && rows.length === 0 && (
            <p className="text-sm text-muted-foreground" role="status">
              {t(`${fp}.empty.${status}`)}
            </p>
          )}

          {query.isSuccess && rows.length > 0 && (
            <DataTable aria-label={t(`${fp}.heading`)}>
              <FederatedIdentitiesTable
                rows={rows}
                onLink={(row) => {
                  link.reset();
                  setLinkTarget(row);
                }}
                onUnlink={(row) => {
                  unlink.reset();
                  setUnlinkTarget(row);
                }}
                pendingUnlinkGuid={unlink.isPending && unlinkTarget !== null ? unlinkTarget.guid : null}
              />
            </DataTable>
          )}
        </section>
      </div>

      <LinkIdentityDialog
        open={linkTarget !== null}
        identity={linkTarget}
        onOpenChange={(next) => {
          if (!next) closeLink();
        }}
        onSubmit={(userGuid) => {
          if (linkTarget === null) return;
          link.mutate({ guid: linkTarget.guid, userGuid }, { onSuccess: closeLink });
        }}
        pending={link.isPending}
        submitError={link.error}
      />

      <DeleteAuthConfirmDialog
        open={unlinkIsPending}
        title={t(
          unlinkTarget?.status === 'PENDING' ? `${fp}.dismiss.title` : `${fp}.unlink.title`,
        )}
        message={t(
          unlinkTarget?.status === 'PENDING' ? `${fp}.dismiss.message` : `${fp}.unlink.message`,
          { identity: unlinkLabel },
        )}
        cancelLabel={t(`${fp}.unlink.cancel`)}
        confirmLabel={t(
          unlinkTarget?.status === 'PENDING' ? `${fp}.dismiss.confirm` : `${fp}.unlink.confirm`,
        )}
        onCancel={() => {
          if (unlink.isPending) return;
          closeUnlink();
        }}
        onConfirm={() => {
          if (unlinkTarget === null) return;
          unlink.mutate(unlinkTarget.guid, { onSuccess: closeUnlink });
        }}
        pending={unlink.isPending}
        submitError={unlink.error}
        submitErrorLabel={(code) => t(`${fp}.unlink.submitError`, { code })}
      />
    </>
  );
}

function RowsSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
